/**
 * AlyzeUserLogin.tsx
 * 
 * Reusable login/profile component that can be imported by other components.
 * 
 * Features:
 * - Shows login button when user is not authenticated
 * - Shows profile icon and email when user is authenticated
 * - Handles auth redirect with path storage
 * - Supports both desktop and mobile layouts
 * - Links to my.alyze.health for authenticated users
 * 
 * Props:
 * - variant: 'desktop' | 'mobile' - controls styling for different layouts
 */

import React from 'react';
import { useAuth } from 'react-oidc-context';
import { makeStyles } from '@fluentui/react-components';
import { Person24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
    profileNav: {
        paddingTop: '0px'
    },
    desktopAuthLink: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textDecoration: 'none',
    },
    desktopLoginButton: {
        backgroundColor: 'transparent',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '16px',
        fontFamily: 'Figtree',
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    desktopEmail: {
        color: 'white',
        fontSize: '16px',
        fontFamily: 'Figtree',
    },
    mobileContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    mobileLoginButton: {
        backgroundColor: 'transparent',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontFamily: 'Figtree',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    mobileEmail: {
        color: 'white',
        fontSize: '12px',
        fontFamily: 'Figtree',
    },
});

interface AlyzeUserLoginProps {
    variant?: 'desktop' | 'mobile';
}

export const AlyzeUserLogin: React.FC<AlyzeUserLoginProps> = ({ variant = 'desktop' }) => {
    const styles = useStyles();

    // Safely handle auth context
    let auth;
    try {
        auth = useAuth();
    } catch (error) {
        console.warn('Auth context error:', error);
        auth = { isAuthenticated: false, user: null, signinRedirect: () => Promise.resolve() } as any;
    }

    const handleLogin = () => {
        try {
            // Store current path for redirect after login
            const currentPath = window.location.pathname + window.location.search + window.location.hash;
            sessionStorage.setItem('auth.redirectPath', currentPath);
            auth.signinRedirect().catch((error: any) => {
                console.error('Login redirect failed:', error);
            });
        } catch (error) {
            console.error('Login handler error:', error);
            // Fallback to direct navigation
            window.location.href = '/auth/callback';
        }
    };

    // Profile icon SVG
    const ProfileIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.85 17.1C6.7 16.45 7.65 15.9375 8.7 15.5625C9.75 15.1875 10.85 15 12 15C13.15 15 14.25 15.1875 15.3 15.5625C16.35 15.9375 17.3 16.45 18.15 17.1C18.7333 16.4167 19.1875 15.6417 19.5125 14.775C19.8375 13.9083 20 12.9833 20 12C20 9.78333 19.2208 7.89583 17.6625 6.3375C16.1042 4.77917 14.2167 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 12.9833 4.1625 13.9083 4.4875 14.775C4.8125 15.6417 5.26667 16.4167 5.85 17.1ZM12 13C11.0167 13 10.1875 12.6625 9.5125 11.9875C8.8375 11.3125 8.5 10.4833 8.5 9.5C8.5 8.51667 8.8375 7.6875 9.5125 7.0125C10.1875 6.3375 11.0167 6 12 6C12.9833 6 13.8125 6.3375 14.4875 7.0125C15.1625 7.6875 15.5 8.51667 15.5 9.5C15.5 10.4833 15.1625 11.3125 14.4875 11.9875C13.8125 12.6625 12.9833 13 12 13ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22ZM12 20C12.8833 20 13.7167 19.8708 14.5 19.6125C15.2833 19.3542 16 18.9833 16.65 18.5C16 18.0167 15.2833 17.6458 14.5 17.3875C13.7167 17.1292 12.8833 17 12 17C11.1167 17 10.2833 17.1292 9.5 17.3875C8.71667 17.6458 8 18.0167 7.35 18.5C8 18.9833 8.71667 19.3542 9.5 19.6125C10.2833 19.8708 11.1167 20 12 20ZM12 11C12.4333 11 12.7917 10.8583 13.075 10.575C13.3583 10.2917 13.5 9.93333 13.5 9.5C13.5 9.06667 13.3583 8.70833 13.075 8.425C12.7917 8.14167 12.4333 8 12 8C11.5667 8 11.2083 8.14167 10.925 8.425C10.6417 8.70833 10.5 9.06667 10.5 9.5C10.5 9.93333 10.6417 10.2917 10.925 10.575C11.2083 10.8583 11.5667 11 12 11Z" fill="white" />
        </svg>
    );

    // Desktop variant
    if (variant === 'desktop') {
        return (
            <div className={styles.profileNav}>
                {auth.isAuthenticated ? (
                    <a 
                        href="https://my.alyze.health" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles.desktopAuthLink}
                    >
                        <ProfileIcon />
                        {auth.user?.profile?.email && (
                            <span className={styles.desktopEmail}>
                                {auth.user.profile.email}
                            </span>
                        )}
                    </a>
                ) : (
                    <button onClick={handleLogin} className={styles.desktopLoginButton}>
                        <ProfileIcon />
                        Login
                    </button>
                )}
            </div>
        );
    }

    // Mobile variant
    return (
        <div className={styles.mobileContainer}>
            {auth.isAuthenticated ? (
                <>
                    <a href="https://my.alyze.health" target="_blank" rel="noopener noreferrer">
                        <Person24Regular color="white" />
                    </a>
                    {auth.user?.profile?.email && (
                        <span className={styles.mobileEmail}>
                        
                        </span>
                    )}
                </>
            ) : (
                <button onClick={handleLogin} className={styles.mobileLoginButton}>
                    <Person24Regular color="white" />                    
                </button>
            )}
        </div>
    );
};
